import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import Modal from "../Modal";
import { validatePlanForm, buildPlanPayload } from "./planFormHelpers";

const EMPTY_PLAN = {
  id: "",
  name: "",
  price: 0,
  sortOrder: 0,
  stripePriceId: "",
  isPrivate: false,
  limitWidgets: 1,
  limitKnowledgeSources: 3,
  monthlyHaikuCredits: 100,
  monthlySonnetCredits: 20,
  // [I3 FIX] Default to null (unlimited) — 0 means "no credits = blocked", which
  // is a valid but surprising default. Admins uncheck "Unlimited" to set a cap.
  monthlyOpusCredits: null,
  // [B1 FIX] Legacy fields removed from UI but still required by backend updatePlan.
  // Backend converts undefined → null via `?? null`, which would silently wipe
  // existing values. Preserve and pass through on submit to prevent data corruption.
  allowedModelFamilies: null,
  limitMessagesPerMonth: null,
  features: [],
};

/**
 * Modal form for creating or editing a plan.
 * Supports nullable limit fields (null = unlimited) and nullable price (null = custom).
 */
const PlanForm = ({
  isOpen,
  onClose,
  onSubmit,
  plan,
  isSubmitting,
  allPlans,
}) => {
  const isEditing = !!plan;
  const [formData, setFormData] = useState(EMPTY_PLAN);
  const [error, setError] = useState("");

  // Stable key counter for feature list items (avoids index-as-key bugs)
  const featureKeyRef = useRef(0);

  // Derive unlimited/custom states from formData — no separate useState needed
  const unlimitedWidgets = formData.limitWidgets === null;
  const unlimitedKnowledge = formData.limitKnowledgeSources === null;
  const customPricing = formData.price === null;
  const unlimitedHaiku = formData.monthlyHaikuCredits === null;
  const unlimitedSonnet = formData.monthlySonnetCredits === null;
  const unlimitedOpus = formData.monthlyOpusCredits === null;

  /**
   * Toggle a field between null (unlimited/custom) and a default numeric value.
   */
  const toggleUnlimited = (field, defaultVal) => {
    setFormData((prev) => ({
      ...prev,
      [field]: prev[field] === null ? defaultVal : null,
    }));
  };

  // Build sort order hint from sibling plans
  const sortOrderHint = (() => {
    if (!allPlans || allPlans.length === 0) return null;
    const others = allPlans
      .filter((p) => !isEditing || p.id !== plan?.id)
      .map((p) => `${p.name}: ${p.sortOrder}`)
      .join(", ");
    return others || null;
  })();

  useEffect(() => {
    if (plan) {
      const rawFeatures = plan.features.length > 0 ? plan.features : [""];
      setFormData({
        id: plan.id,
        name: plan.name,
        price: plan.price, // null preserved for custom pricing
        sortOrder: plan.sortOrder,
        stripePriceId: plan.stripePriceId || "",
        isPrivate: plan.isPrivate || false,
        limitWidgets: plan.limits.widgets, // null preserved for unlimited
        limitKnowledgeSources: plan.limits.knowledgeSources,
        monthlyHaikuCredits: plan.monthlyCredits?.haiku ?? null,
        monthlySonnetCredits: plan.monthlyCredits?.sonnet ?? null,
        monthlyOpusCredits: plan.monthlyCredits?.opus ?? null,
        // [B1 FIX] Preserve legacy fields so updatePlan doesn't wipe them
        allowedModelFamilies: plan.allowedModelFamilies,
        limitMessagesPerMonth: plan.limits.messagesPerMonth,
        features: rawFeatures.map((f) => ({
          key: featureKeyRef.current++,
          value: f,
        })),
      });
    } else {
      setFormData({
        ...EMPTY_PLAN,
        features: [{ key: featureKeyRef.current++, value: "" }],
      });
    }
    setError("");
  }, [plan, isOpen]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleNumberChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value === "" ? "" : Number(value),
    }));
  };

  const handleFeatureChange = (index, value) => {
    setFormData((prev) => {
      const features = [...prev.features];
      features[index] = { ...features[index], value };
      return { ...prev, features };
    });
  };

  const addFeature = () => {
    setFormData((prev) => ({
      ...prev,
      features: [...prev.features, { key: featureKeyRef.current++, value: "" }],
    }));
  };

  const removeFeature = (index) => {
    setFormData((prev) => ({
      ...prev,
      features: prev.features.filter((_, i) => i !== index),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    const validationError = validatePlanForm(formData, isEditing);
    if (validationError) {
      setError(validationError);
      return;
    }

    const payload = buildPlanPayload(formData, isEditing);

    try {
      await onSubmit(payload, isEditing ? plan.id : null);
    } catch (err) {
      setError(
        err.response?.data?.message || err.message || "Failed to save plan",
      );
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={isEditing ? `Edit Plan: ${plan.name}` : "Create New Plan"}
      size="large"
    >
      <form onSubmit={handleSubmit} className="admin-plan-form">
        {error && <div className="admin-form-error">{error}</div>}

        <div className="admin-form-row">
          <div className="admin-form-group">
            <label htmlFor="plan-id">Plan ID (slug)</label>
            <input
              id="plan-id"
              name="id"
              type="text"
              value={formData.id}
              onChange={handleChange}
              disabled={isEditing}
              placeholder="e.g. starter, pro-plus"
              required
            />
            {isEditing && (
              <small className="admin-form-hint">ID cannot be changed</small>
            )}
          </div>

          <div className="admin-form-group">
            <label htmlFor="plan-name">Display Name</label>
            <input
              id="plan-name"
              name="name"
              type="text"
              value={formData.name}
              onChange={handleChange}
              placeholder="e.g. Starter, Pro Plus"
              required
            />
          </div>
        </div>

        <div className="admin-form-row">
          <div className="admin-form-group">
            <label htmlFor="plan-price">
              Price ($/month)
              <span className="admin-checkbox-inline">
                <input
                  type="checkbox"
                  checked={customPricing}
                  onChange={() => toggleUnlimited("price", 0)}
                />
                Custom pricing
              </span>
            </label>
            <input
              id="plan-price"
              name="price"
              type="number"
              min="0"
              step="0.01"
              value={customPricing ? "" : formData.price}
              onChange={handleNumberChange}
              disabled={customPricing}
            />
          </div>

          <div className="admin-form-group">
            <label htmlFor="plan-sort-order">Sort Order</label>
            <input
              id="plan-sort-order"
              name="sortOrder"
              type="number"
              min="0"
              value={formData.sortOrder}
              onChange={handleNumberChange}
              required
            />
            <small className="admin-form-hint">
              Higher = higher tier. Must be unique.
              {sortOrderHint && ` Current: ${sortOrderHint}`}
            </small>
          </div>
        </div>

        <div className="admin-form-group">
          <label htmlFor="plan-stripe-price-id">Stripe Price ID</label>
          <input
            id="plan-stripe-price-id"
            name="stripePriceId"
            type="text"
            value={formData.stripePriceId}
            onChange={handleChange}
            placeholder="price_..."
          />
          <small className="admin-form-hint">
            Required for plans purchasable via checkout
          </small>
        </div>

        <div className="admin-form-group">
          <label className="admin-checkbox-label">
            <input
              type="checkbox"
              checked={formData.isPrivate}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  isPrivate: e.target.checked,
                }))
              }
            />
            Private Plan
          </label>
          <small className="admin-form-hint">
            Private plans are hidden from the public billing page and require an
            admin-generated invite to access.
          </small>
        </div>

        <fieldset className="admin-fieldset">
          <legend>Limits</legend>
          <div className="admin-form-row">
            <div className="admin-form-group">
              <label htmlFor="plan-limit-widgets">
                Widgets
                <span className="admin-checkbox-inline">
                  <input
                    type="checkbox"
                    checked={unlimitedWidgets}
                    onChange={() => toggleUnlimited("limitWidgets", 1)}
                  />
                  Unlimited
                </span>
              </label>
              <input
                id="plan-limit-widgets"
                name="limitWidgets"
                type="number"
                min="0"
                value={unlimitedWidgets ? "" : formData.limitWidgets}
                onChange={handleNumberChange}
                disabled={unlimitedWidgets}
              />
            </div>

            <div className="admin-form-group">
              <label htmlFor="plan-limit-knowledge">
                Knowledge Sources
                <span className="admin-checkbox-inline">
                  <input
                    type="checkbox"
                    checked={unlimitedKnowledge}
                    onChange={() => toggleUnlimited("limitKnowledgeSources", 3)}
                  />
                  Unlimited
                </span>
              </label>
              <input
                id="plan-limit-knowledge"
                name="limitKnowledgeSources"
                type="number"
                min="0"
                value={unlimitedKnowledge ? "" : formData.limitKnowledgeSources}
                onChange={handleNumberChange}
                disabled={unlimitedKnowledge}
              />
            </div>
          </div>
        </fieldset>

        <fieldset className="admin-fieldset">
          <legend>Monthly Credit Allocations</legend>
          <small
            className="admin-form-hint"
            style={{ marginBottom: 8, display: "block" }}
          >
            Credits granted each billing cycle. Users spend credits per message.
            Blank/unlimited = no cap.
          </small>
          <div className="admin-form-row admin-form-row-thirds">
            <div className="admin-form-group">
              <label htmlFor="plan-credits-haiku">
                Haiku Credits
                <span className="admin-checkbox-inline">
                  <input
                    type="checkbox"
                    checked={unlimitedHaiku}
                    onChange={() => toggleUnlimited("monthlyHaikuCredits", 100)}
                  />
                  Unlimited
                </span>
              </label>
              <input
                id="plan-credits-haiku"
                name="monthlyHaikuCredits"
                type="number"
                min="0"
                value={unlimitedHaiku ? "" : formData.monthlyHaikuCredits}
                onChange={handleNumberChange}
                disabled={unlimitedHaiku}
              />
            </div>

            <div className="admin-form-group">
              <label htmlFor="plan-credits-sonnet">
                Sonnet Credits
                <span className="admin-checkbox-inline">
                  <input
                    type="checkbox"
                    checked={unlimitedSonnet}
                    onChange={() => toggleUnlimited("monthlySonnetCredits", 20)}
                  />
                  Unlimited
                </span>
              </label>
              <input
                id="plan-credits-sonnet"
                name="monthlySonnetCredits"
                type="number"
                min="0"
                value={unlimitedSonnet ? "" : formData.monthlySonnetCredits}
                onChange={handleNumberChange}
                disabled={unlimitedSonnet}
              />
            </div>

            <div className="admin-form-group">
              <label htmlFor="plan-credits-opus">
                Opus Credits
                <span className="admin-checkbox-inline">
                  <input
                    type="checkbox"
                    checked={unlimitedOpus}
                    onChange={() => toggleUnlimited("monthlyOpusCredits", 5)}
                  />
                  Unlimited
                </span>
              </label>
              <input
                id="plan-credits-opus"
                name="monthlyOpusCredits"
                type="number"
                min="0"
                value={unlimitedOpus ? "" : formData.monthlyOpusCredits}
                onChange={handleNumberChange}
                disabled={unlimitedOpus}
              />
            </div>
          </div>
        </fieldset>

        <fieldset className="admin-fieldset">
          <legend>Features (display list)</legend>
          {formData.features.map((feature, index) => (
            <div key={feature.key} className="admin-feature-row">
              <input
                type="text"
                value={feature.value}
                onChange={(e) => handleFeatureChange(index, e.target.value)}
                placeholder="e.g. 5 widgets, Email support"
              />
              {formData.features.length > 1 && (
                <button
                  type="button"
                  className="admin-btn admin-btn-remove"
                  onClick={() => removeFeature(index)}
                  aria-label="Remove feature"
                >
                  &#x2715;
                </button>
              )}
            </div>
          ))}
          <button
            type="button"
            className="admin-btn admin-btn-add-feature"
            onClick={addFeature}
          >
            + Add Feature
          </button>
        </fieldset>

        <div className="admin-form-actions">
          <button
            type="button"
            className="admin-btn admin-btn-cancel"
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="admin-btn admin-btn-primary"
            disabled={isSubmitting}
          >
            {isSubmitting
              ? "Saving..."
              : isEditing
                ? "Update Plan"
                : "Create Plan"}
          </button>
        </div>
      </form>
    </Modal>
  );
};

PlanForm.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  plan: PropTypes.object, // null for create, plan object for edit
  isSubmitting: PropTypes.bool,
  allPlans: PropTypes.array, // all plans for sort order hint
};

export default PlanForm;
